/**
 * ZoomControls component (Task Group 1 - Task 1.3)
 *
 * Provides zoom in/out buttons for the timeline.
 *
 * Features:
 * - Zoom in (+) button to increase pixelsPerSecond
 * - Zoom out (-) button to decrease pixelsPerSecond
 * - Display of current zoom level
 * - Consistent styling with TimelineControls and Add Track button
 *
 * Zoom range: 0.5-100 pixels per second (logarithmic scaling)
 * - Max (100 px/s): detailed view
 * - Min (0.5 px/s): 1 hour fits in ~1800px viewport
 */

import { Plus, Minus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useUiStore, ZOOM_MIN, ZOOM_MAX } from '../../stores/uiStore';

export function ZoomControls() {
  const pixelsPerSecond = useUiStore((state) => state.pixelsPerSecond);
  const zoomIn = useUiStore((state) => state.zoomIn);
  const zoomOut = useUiStore((state) => state.zoomOut);

  // Check if buttons should be disabled at boundaries
  const isAtMinZoom = pixelsPerSecond <= ZOOM_MIN;
  const isAtMaxZoom = pixelsPerSecond >= ZOOM_MAX;

  // Format zoom level for display
  // Show 1 decimal for values < 10, otherwise round to integer
  const zoomDisplay =
    pixelsPerSecond < 10
      ? pixelsPerSecond.toFixed(1)
      : Math.round(pixelsPerSecond).toString();

  return (
    <div
      className="flex items-center gap-1"
      data-testid="zoom-controls"
      role="group"
      aria-label="Timeline zoom controls"
    >
      {/* Zoom out button */}
      <motion.button
        className="flex items-center justify-center w-6 h-6 rounded transition-colors"
        style={{
          backgroundColor: isAtMinZoom
            ? 'var(--color-dark-bg)'
            : 'var(--color-dark-panel)',
          color: isAtMinZoom
            ? 'var(--color-dark-text-muted)'
            : 'var(--color-dark-text)',
          cursor: isAtMinZoom ? 'not-allowed' : 'pointer',
        }}
        onClick={isAtMinZoom ? undefined : zoomOut}
        disabled={isAtMinZoom}
        title="Zoom out (Ctrl+Scroll Down)"
        aria-label="Zoom out"
        whileHover={isAtMinZoom ? {} : { backgroundColor: '#3a3a3a' }}
        whileTap={isAtMinZoom ? {} : { scale: 0.95 }}
        data-testid="zoom-out-button"
      >
        <Minus className="h-3.5 w-3.5" />
      </motion.button>

      {/* Zoom level display */}
      <span
        className="text-xs font-mono text-gray-400 min-w-[40px] text-center select-none"
        data-testid="zoom-level"
        title={`${zoomDisplay} pixels per second`}
      >
        {zoomDisplay}
      </span>

      {/* Zoom in button */}
      <motion.button
        className="flex items-center justify-center w-6 h-6 rounded transition-colors"
        style={{
          backgroundColor: isAtMaxZoom
            ? 'var(--color-dark-bg)'
            : 'var(--color-dark-panel)',
          color: isAtMaxZoom
            ? 'var(--color-dark-text-muted)'
            : 'var(--color-dark-text)',
          cursor: isAtMaxZoom ? 'not-allowed' : 'pointer',
        }}
        onClick={isAtMaxZoom ? undefined : zoomIn}
        disabled={isAtMaxZoom}
        title="Zoom in (Ctrl+Scroll Up)"
        aria-label="Zoom in"
        whileHover={isAtMaxZoom ? {} : { backgroundColor: '#3a3a3a' }}
        whileTap={isAtMaxZoom ? {} : { scale: 0.95 }}
        data-testid="zoom-in-button"
      >
        <Plus className="h-3.5 w-3.5" />
      </motion.button>
    </div>
  );
}
